import ModalSurface from './ModalSurface';
import { useEffect, useRef, useState } from 'react';
import { RECOVERY_NOTICE, requestAuthRecovery, validRecoveryEmail } from '../../lib/authRecovery';
import AuthPill from './AuthPill';

export default function AuthRecoveryDialog({ onClose, client }) {
    const [kind, setKind] = useState(null);
    const [email, setEmail] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const dialog = useRef(null);
    const emailInput = useRef(null);
    const submitting = useRef(false);

    useEffect(() => {
        const previousFocus = document.activeElement;
        dialog.current.querySelector('button').focus();
        return () => previousFocus?.focus?.();
    }, []);
    useEffect(() => { if (kind) emailInput.current?.focus(); }, [kind]);

    async function submit(event) {
        event.preventDefault();
        if (submitting.current) return;
        if (!validRecoveryEmail(email)) {
            setFieldError('Enter a valid email address.');
            emailInput.current?.focus();
            return;
        }
        setFieldError(''); setError(''); setBusy(true); submitting.current = true;
        try {
            await requestAuthRecovery(client, kind, email);
            setSent(true);
        } catch (e) { setError(e.message); }
        finally { setBusy(false); submitting.current = false; }
    }

    function onKeyDown(event) {
        if (event.key === 'Escape') { event.stopPropagation(); if (!submitting.current) onClose(); }
        if (event.key !== 'Tab') return;
        const controls = [...dialog.current.closest('[role=dialog]').querySelectorAll('button:not(:disabled), input:not(:disabled)')].filter(control => !control.classList.contains('ia-modal-close') || window.matchMedia?.('(max-width: 767px)')?.matches);
        const first = controls[0]; const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    return <ModalSurface panelProps={{ 'data-m7-touch': true }} onClose={() => { if (!submitting.current) onClose(); }} label="Sign-in recovery" labelledBy="recovery-title" busy={busy} closeDisabled={busy}
        overlayClassName="auth-recovery-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        panelClassName="auth-recovery-dialog w-full max-w-md rounded-xl bg-white p-6 text-gray-800 shadow-xl">
        <div ref={dialog} aria-busy={busy} onKeyDown={onKeyDown} className="ia-modal-layout">
            <h2 id="recovery-title" className="ia-modal-title mb-4 text-xl font-semibold">Can't sign in / Forgot password?</h2>
            {!kind ? <div className="ia-modal-body flex flex-col gap-3">
                <AuthPill onClick={() => setKind('invite')}>Can't Sign In</AuthPill>
                <AuthPill onClick={() => setKind('recovery')}>Forgot Password</AuthPill>
            </div> : sent ? <div className="ia-modal-body space-y-3">
                <AuthPill success className="w-full">Instructions sent</AuthPill>
                <p role="status">{RECOVERY_NOTICE}</p>
            </div> : <form noValidate onSubmit={submit} className="space-y-3">
                <div className="ia-modal-body space-y-3">
                <p>{kind === 'invite' ? "Can't Sign In" : 'Forgot Password'}</p>
                <label htmlFor="recovery-email" className="block">Email</label>
                <input ref={emailInput} id="recovery-email" type="email" autoComplete="email" value={email}
                    disabled={busy} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? 'recovery-email-error' : undefined}
                    className={`w-full rounded border p-3 ${fieldError ? 'border-red-600' : 'border-gray-300'}`}
                    onChange={(event) => { setEmail(event.target.value); setFieldError(''); }} />
                {fieldError && <p id="recovery-email-error" role="alert" className="text-red-600">{fieldError}</p>}
                {error && <p role="alert" className="text-red-600">{error}</p>}
                </div>
                <div className="ia-modal-actions">
                <AuthPill type="submit" disabled={busy} className="w-full">
                    {busy ? 'Sending...' : 'Send instructions'}</AuthPill>
                </div>
            </form>}
            <AuthPill disabled={busy} onClick={onClose} className="ia-modal-dismiss mt-4">Close</AuthPill>
        </div>
    </ModalSurface>;
}
